<template>
  <div>
    <v-dialog v-model="dialog" fullscreen transition="dialog-bottom-transition">
      <v-container fluid class="py-0 fill-height">
        <v-row class="fill-height">
          <v-col cols="12" class="px-0 py-0">
            <v-card height="100%">
              <v-toolbar
                color="indigo darken-2"
                :extension-height="toolbarExtensionHeight"
                dark>
                <template v-slot:extension v-if="isDebug && loaded && !isDefaultSettings">
                  <span>profile: {{ profile.name }}, user_id: {{ profile.userId }}, profile_id: {{ profile.idProfile }}, settings_id: {{
                      settings.idSettings
                    }}, build: {{ settings.buildVersion }}</span>
                </template>
                <v-toolbar-title>
                  <v-icon left>{{ accountIcon }}</v-icon>
                  <template v-if="!isDefaultSettings">
                    <span
                      v-if="!isDefaultSettings">{{
                        $vuetify.lang.t('$vuetify.map.driverSettings.title', username)
                      }}</span>
                  </template>
                  <template v-else>
                    <span>{{
                        $vuetify.lang.t('$vuetify.map.driverSettings.defaultSettingsTitle', settings.idSettings)
                      }}</span>
                  </template>
                </v-toolbar-title>

                <v-spacer></v-spacer>

                <v-switch label="Debug" v-if="isDebugAvailable" v-model="state.debug" hide-details></v-switch>

                <v-tooltip bottom v-if="isAdmin && profile.nanoUser">
                  <template v-slot:activator="{ on }">
                    <v-btn icon class="text-right ml-2" v-on="on" @click="state.syncConfirmationDialog = true">
                      <!-- syncProfile -->
                      <v-icon>{{ syncIcon }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $vuetify.lang.t('$vuetify.map.driverSettings.sync') }}</span>
                </v-tooltip>

                <v-btn icon class="text-right" @click="closeDialog">
                  <v-icon>{{ closeIcon }}</v-icon>
                </v-btn>
              </v-toolbar>

              <v-progress-linear :active="state.requestLoading" indeterminate absolute></v-progress-linear>

              <v-container fluid>
                <v-skeleton-loader :loading="!loaded" type="card">
                  <driver-settings-searcher v-if="isDefaultSettings" :fine-tuning-settings="fineTuningSettings"
                                            :tuneSettings="isFineTuning"
                                            @driver-sync-success="driverSyncSuccess"
                                            @fine-tuning-changed="fineTuningChanged"
                                            @driver-sync-failure="driverSyncFailure"
                                            @sync-event-start="state.requestLoading = true"
                                            @sync-event-finish="state.requestLoading = false"
                  ></driver-settings-searcher>

                  <v-tabs :vertical="mobile" :fixed-tabs="!mobile" v-model="tab"
                          :show-arrows="$vuetify.breakpoint.smAndDown">
                    <v-tab :class="{'justify-start': mobile}">
                      <v-icon left>{{ dangerousStateIcons[DangerousState.DROWSINESS] }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.map.driverSettings.tabs.drowsiness') }}
                    </v-tab>
                    <v-tab :class="{'justify-start': mobile}" v-if="isDefaultSettings || !settings.distractionSplitted">
                      <v-icon left>{{ dangerousStateIcons[DangerousState.DISTRACTION] }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.map.driverSettings.tabs.distraction') }}
                    </v-tab>
                    <v-tab :class="{'justify-start': mobile}" v-if="isDefaultSettings || settings.distractionSplitted">
                      <v-icon left>{{ dangerousStateIcons[DangerousState.DISTRACTION_NO_FACE] }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.map.driverSettings.tabs.distractionNoFace') }}
                    </v-tab>
                    <v-tab :class="{'justify-start': mobile}" v-if="isDefaultSettings || settings.distractionSplitted">
                      <v-icon left>{{ dangerousStateIcons[DangerousState.DISTRACTION_NO_ATTENTION] }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.map.driverSettings.tabs.distractionNoAttention') }}
                    </v-tab>
                    <v-tab :class="{'justify-start': mobile}">
                      <v-icon left>{{ dangerousStateIcons[DangerousState.BELT_NOT_FASTEN] }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.map.driverSettings.tabs.belt') }}
                    </v-tab>
                    <v-tab :class="{'justify-start': mobile}">
                      <v-icon left>{{ dangerousStateIcons[DangerousState.CELLPHONE_USE] }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.map.driverSettings.tabs.cellphone') }}
                    </v-tab>
                    <v-tab :class="{'justify-start': mobile}">
                      <v-icon left>{{ dangerousStateIcons[DangerousState.EATING] }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.map.driverSettings.tabs.eating') }}
                    </v-tab>
                    <v-tab :class="{'justify-start': mobile}">
                      <v-icon left>{{ dangerousStateIcons[DangerousState.SMOKING] }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.map.driverSettings.tabs.smoking') }}
                    </v-tab>
                    <v-tab :class="{'justify-start': mobile}">
                      <v-icon left>{{ dangerousStateIcons[DangerousState.CAMERA_SABOTAGE] }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.map.driverSettings.tabs.sabotage') }}
                    </v-tab>
                    <v-tab :class="{'justify-start': mobile}">
                      <v-icon left>{{ notificationsIcon }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.map.driverSettings.tabs.notifications') }}
                    </v-tab>
                    <v-tab :class="{'justify-start': mobile}">
                      <v-icon left>{{ generalIcon }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.map.driverSettings.tabs.general') }}
                    </v-tab>

                    <!-- drowsiness item -->
                    <v-tabs-items v-model="tab" :touchless="$vuetify.breakpoint.smAndDown">
                      <v-tab-item>
                        <v-card flat>
                          <v-row class="mx-auto fill-height" no-gutters>
                            <v-col cols="12" md="6">
                              <v-switch class="mx-2 text-center"
                                        :label="$vuetify.lang.t('$vuetify.map.driverSettings.labels.isDetectDrowsiness')"
                                        :placeholder="getPlaceholder('isDetectDrowsiness')"
                                        :messages="isDebug ? 'isDetectDrowsiness' : ''"
                                        v-model="settings.isDetectDrowsiness">
                                <v-checkbox v-show="isFineTuning" class="shrink mt-0" slot="prepend"
                                            v-model="fineTuningSettings['isDetectDrowsiness']"></v-checkbox>
                              </v-switch>
                            </v-col>
                          </v-row>
                          <v-card-subtitle>
                            <v-icon left>{{ filtersIcon }}</v-icon>
                            <span class="font-weight-bold">{{
                                $vuetify.lang.t('$vuetify.map.driverSettings.tabContentTitles.filters')
                              }}</span>
                          </v-card-subtitle>
                          <v-card-text class="px-0 px-md-4">
                            <v-row class="mx-auto fill-height" no-gutters>
                              <v-col cols="12" md="6" class="px-2  mt-n5" align-self="end"
                                     v-for="entry in drowsinessFilters"
                                     :key="`drowsiness-filter-${entry.field}`">
                                <v-card-text class="mx-n2 caption" v-if="entry.hasOwnProperty('field')">
                                  {{
                                    $vuetify.lang.t(`$vuetify.map.driverSettings.labels.${entry.hasOwnProperty('label')
                                      ? entry.label : entry.field}`)
                                  }}
                                </v-card-text>
                                <v-text-field
                                  :placeholder="getPlaceholder(entry.field)"
                                  class="mt-n8"
                                  type="number" step="1"
                                  :rules="timeRules"
                                  :messages="isDebug ? entry.field : ''"
                                  v-model.number="settings[entry.field]">
                                  <v-checkbox v-show="isFineTuning" class="shrink mt-0" slot="prepend"
                                              v-model="fineTuningSettings[entry.field]"></v-checkbox>
                                </v-text-field>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>

                        <v-card flat>
                          <v-card-subtitle>
                            <v-icon left>{{ settingsIcon }}</v-icon>
                            <span class="font-weight-bold">{{
                                $vuetify.lang.t('$vuetify.map.driverSettings.tabContentTitles.settings')
                              }}</span>
                          </v-card-subtitle>
                          <v-card-text class="px-0 px-md-4">
                            <v-row class="mx-auto fill-height" justify="start" no-gutters>
                              <template v-for="entry in drowsinessSettings">
                                <v-col cols="12" :md="entry.hasOwnProperty('row') && entry.row ? 12 : 6"
                                       align-self="end"
                                       class="px-2 mt-n5" :key="entry.field">
                                  <!-- create component which reuse structure -->
                                  <template v-if="entry.hasOwnProperty('field')">
                                    <template v-if="entry.hasOwnProperty('type') && entry.type === 'slider'">
                                      <v-card-subtitle class="mb-3">{{
                                          $vuetify.lang.t(`$vuetify.map.driverSettings.labels.${entry.field}`)
                                        }}
                                      </v-card-subtitle>
                                      <v-slider thumb-label="always" :min="entry.min" :max="entry.max"
                                                class="slider"
                                                :placeholder="getPlaceholder(entry.field)"
                                                :messages="isDebug ? entry.field : ''"
                                                v-model.number="settings[entry.field]">
                                        <v-checkbox v-show="isFineTuning" class="shrink mt-0" slot="prepend"
                                                    v-model="fineTuningSettings[entry.field]"></v-checkbox>
                                      </v-slider>
                                    </template>
                                    <template v-else>
                                      <v-card-text class="mx-n2 caption" v-if="entry.hasOwnProperty('field')">
                                        {{ $vuetify.lang.t(`$vuetify.map.driverSettings.labels.${entry.field}`) }}
                                      </v-card-text>
                                      <v-text-field
                                        :placeholder="getPlaceholder(entry.field)"
                                        class="mt-n8"
                                        type="number"
                                        :messages="isDebug ? entry.field : ''"
                                        :step="entry.hasOwnProperty('step') ? entry.step : 1"
                                        :rules="entry.rules"
                                        v-model.number="settings[entry.field]">
                                        <v-checkbox v-show="isFineTuning" class="shrink mt-0" slot="prepend"
                                                    v-model="fineTuningSettings[entry.field]"></v-checkbox>
                                      </v-text-field>
                                    </template>
                                  </template>
                                  <template v-else>
                                    <v-card-subtitle v-if="entry.label" class="font-weight-black">{{
                                        $vuetify.lang.t(entry.label)
                                      }}
                                    </v-card-subtitle>
                                  </template>
                                </v-col>
                              </template>
                            </v-row>
                          </v-card-text>
                        </v-card>

                      </v-tab-item>

                      <!-- distraction item -->
                      <v-tab-item v-if="isDefaultSettings || !settings.distractionSplitted">
                        <state-settings :settings="settings" detection="isDetectDistraction"
                                        :fine-tuning-settings="fineTuningSettings"
                                        :is-fine-tuning="isFineTuning"
                                        :is-debug="isDebug"
                                        :state-filters="distractionFilters" :state-settings="distractionSettings">
                        </state-settings>
                      </v-tab-item>

                      <!-- distractionNoFace item -->
                      <v-tab-item v-if="isDefaultSettings || settings.distractionSplitted">
                        <state-settings :settings="settings" detection="isDetectDistractionNoFace"
                                        :fine-tuning-settings="fineTuningSettings"
                                        :is-fine-tuning="isFineTuning"
                                        :is-debug="isDebug"
                                        :state-filters="distractionNoFaceFilters"
                                        :state-settings="distractionNoFaceSettings">
                        </state-settings>
                      </v-tab-item>

                      <!-- distractionNoAttention item -->
                      <v-tab-item v-if="isDefaultSettings || settings.distractionSplitted">
                        <state-settings :settings="settings" detection="isDetectDistractionNoAttention"
                                        :fine-tuning-settings="fineTuningSettings"
                                        :is-fine-tuning="isFineTuning"
                                        :is-debug="isDebug"
                                        :state-filters="distractionNoAttentionFilters"
                                        :state-settings="distractionNoAttentionSettings">
                        </state-settings>
                      </v-tab-item>

                      <!-- belt item -->
                      <v-tab-item>
                        <state-settings :settings="settings" detection="isDetectSeatBelt"
                                        :fine-tuning-settings="fineTuningSettings"
                                        :is-fine-tuning="isFineTuning"
                                        :is-debug="isDebug"
                                        :state-filters="beltFilters" :state-settings="beltSettings">
                        </state-settings>
                      </v-tab-item>

                      <!-- cellphone item -->
                      <v-tab-item>
                        <state-settings :settings="settings" detection="isDetectCellPhone"
                                        :fine-tuning-settings="fineTuningSettings"
                                        :is-fine-tuning="isFineTuning"
                                        :is-debug="isDebug"
                                        :state-filters="cellPhoneFilters" :state-settings="cellPhoneSettings">
                        </state-settings>
                      </v-tab-item>

                      <!-- eating eating -->
                      <v-tab-item>
                        <state-settings :settings="settings" detection="isDetectEating"
                                        :fine-tuning-settings="fineTuningSettings"
                                        :is-fine-tuning="isFineTuning"
                                        :is-debug="isDebug"
                                        :state-filters="eatingFilters" :state-settings="eatingSettings">
                        </state-settings>
                      </v-tab-item>

                      <!-- smoking eating -->
                      <v-tab-item>
                        <state-settings :settings="settings" detection="isDetectSmoking"
                                        :fine-tuning-settings="fineTuningSettings"
                                        :is-fine-tuning="isFineTuning"
                                        :is-debug="isDebug"
                                        :state-filters="smokingFilters" :state-settings="smokingSettings">
                        </state-settings>
                      </v-tab-item>

                      <!-- sabotage item -->
                      <v-tab-item>
                        <state-settings :settings="settings" detection="isDetectCameraSabotage"
                                        :fine-tuning-settings="fineTuningSettings"
                                        :is-fine-tuning="isFineTuning"
                                        :is-debug="isDebug"
                                        :state-filters="sabotageFilters">
                        </state-settings>
                      </v-tab-item>

                      <!-- notification item -->
                      <v-tab-item>
                        <v-card flat>
                          <v-card-subtitle>
                            <v-icon left>{{ settingsIcon }}</v-icon>
                            <span class="font-weight-bold">{{
                                $vuetify.lang.t('$vuetify.map.driverSettings.tabContentTitles.settings')
                              }}</span>
                          </v-card-subtitle>
                          <v-card-text class="px-0 px-md-4">
                            <v-row class="mx-auto fill-height" no-gutters justify="start">
                              <v-col cols="12" lg="4" md="6" class="px-2" v-for="entry in notificationsSettings"
                                     :key="entry.title">
                                <v-card flat>
                                  <v-card-subtitle>
                                    <v-icon left class="ml-n3">{{ entry.icon }}</v-icon>
                                    <span class="font-weight-bold">{{
                                        $vuetify.lang.t(`$vuetify.map.driverSettings.tabs.${entry.title}`)
                                      }}</span>
                                  </v-card-subtitle>
                                  <v-card-text class="py-0 px-0">
                                    <v-row class="mx-auto fill-height" no-gutters>
                                      <v-col cols="12" md="12">
                                        <v-switch
                                          dense
                                          :label="$vuetify.lang.t(`$vuetify.map.driverSettings.labels.${entry.switchTitle}`)"
                                          :placeholder="getPlaceholder(entry.switch)"
                                          :messages="isDebug ? entry.switch : ''"
                                          v-model.number="settings[entry.switch]">
                                          <v-checkbox v-show="isFineTuning" slot="prepend" class="shrink mt-0" dense
                                                      hide-details
                                                      v-model="fineTuningSettings[entry.switch]"></v-checkbox>
                                        </v-switch>
                                      </v-col>
                                      <v-col cols="12" md="8" v-if="entry.hasOwnProperty('number')" align-self="end">
                                        <v-card-text class="mx-n2 caption">
                                          {{ $vuetify.lang.t(`$vuetify.map.driverSettings.labels.${entry.numberTitle}`) }}
                                        </v-card-text>
                                        <v-text-field
                                          :placeholder="getPlaceholder(entry.number)"
                                          class="mt-n8"
                                          type="number" step="1"
                                          :rules="timeRules"
                                          :messages="isDebug ? entry.number : ''"
                                          v-model.number="settings[entry.number]">
                                          <v-checkbox v-show="isFineTuning" class="shrink mt-0" slot="prepend"
                                                      v-model="fineTuningSettings[entry.number]"></v-checkbox>
                                        </v-text-field>
                                      </v-col>
                                    </v-row>
                                  </v-card-text>
                                </v-card>
                              </v-col>
                            </v-row>
                          </v-card-text>
                        </v-card>
                      </v-tab-item>

                      <!-- general item -->
                      <v-tab-item>
                        <v-card flat>
                          <v-card-text class="px-0 px-md-4">
                            <general-settings title="recommendationAndDangerousStateSettings"
                                              :fields="recommendationAndDangerousStateList" :settings="settings"
                                              :fine-tuning-settings="fineTuningSettings"
                                              :is-debug="isDebug"
                                              :is-fine-tuning="isFineTuning">
                            </general-settings>

                            <general-settings title="detectorsSettings"
                                              :fields="detectorSettingsList" :settings="settings"
                                              :fine-tuning-settings="fineTuningSettings"
                                              :is-debug="isDebug"
                                              :is-fine-tuning="isFineTuning">
                            </general-settings>

                            <general-settings title="detectorsStopSettings"
                                              :fields="detectorStopSettingsList" :settings="settings"
                                              :fine-tuning-settings="fineTuningSettings"
                                              :is-debug="isDebug"
                                              :is-fine-tuning="isFineTuning">
                            </general-settings>

                            <general-settings title="deviceSettings"
                                              :fields="deviceList" :settings="settings"
                                              :fine-tuning-settings="fineTuningSettings"
                                              :is-debug="isDebug"
                                              :is-fine-tuning="isFineTuning">
                            </general-settings>

                            <general-settings title="soundSettings" v-if="!profile.nanoUser"
                                              :fields="soundList" :settings="settings"
                                              :fine-tuning-settings="fineTuningSettings"
                                              :is-debug="isDebug"
                                              :is-fine-tuning="isFineTuning">
                            </general-settings>

                            <general-settings title="statisticsSettings"
                                              :fields="statisticsList" :settings="settings"
                                              :fine-tuning-settings="fineTuningSettings"
                                              :is-debug="isDebug"
                                              :is-fine-tuning="isFineTuning">
                            </general-settings>

                            <general-settings detection="autoCalibrateEnabled" title="autoCalibrate"
                                              :md-cols="4"
                                              :fields="autoCalibrateList" :settings="settings"
                                              :fine-tuning-settings="fineTuningSettings"
                                              :is-debug="isDebug"
                                              :is-fine-tuning="isFineTuning">
                            </general-settings>

                          </v-card-text>
                        </v-card>

                      </v-tab-item>
                    </v-tabs-items>
                  </v-tabs>

                  <v-slide-y-transition :hide-on-leave="true" :leave-absolute="true">
                    <div v-if="isSpecial && state.debug">
                      {{ settings.backendModel() }}
                    </div>
                  </v-slide-y-transition>

                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="success" tile @click="processForm">
                      <v-icon left>{{ buttonIcon }}</v-icon>
                      {{ $vuetify.lang.t('$vuetify.shared.save') }}
                    </v-btn>
                  </v-card-actions>

                </v-skeleton-loader>

              </v-container>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

    </v-dialog>

    <v-dialog v-model="state.unsavedDialog" persistent max-width="350">
      <v-card>
        <v-card-title class="headline" color="warning">
          {{ $vuetify.lang.t('$vuetify.map.driverSettings.unsavedDialog.title') }}
        </v-card-title>
        <v-card-text>{{ $vuetify.lang.t('$vuetify.map.driverSettings.unsavedDialog.text') }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="red darken-2" text @click="dialogCloseRequest">{{ $vuetify.lang.t(`$vuetify.close`) }}</v-btn>
          <v-btn color="green darken-2" text @click="state.unsavedDialog = false">
            {{ $vuetify.lang.t('$vuetify.map.driverSettings.unsavedDialog.return') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <driver-settings-sync-confirmation :dialog="state.syncConfirmationDialog"
                                       :state="SyncDialogStates.SINGLE"
                                       @sync-dialog-confirm="syncProfile"
                                       @sync-dialog-reject="state.syncConfirmationDialog = false"
                                       @sync-dialog-closed="state.syncConfirmationDialog = false">
    </driver-settings-sync-confirmation>

  </div>
</template>

<script>
import { UserProfile } from '@/models/user/UserProfile'
import { UserSettings } from '@/models/user/UserSettings'
import {
  mdiAccountCardDetails, mdiBellRing, mdiBullhorn,
  mdiClose, mdiCogs,
  mdiContentSave, mdiFileSettings, mdiFilter,
  mdiSync
} from '@mdi/js'
import { AUTH_MODULE } from '@/store/modules'
import { createHelpers } from 'vuex-map-fields'

import isEqual from 'lodash.isequal'
import clone from 'lodash.clonedeep'

import backend from '@/backend'
import DriverSettingsSearcher from './driverSettingsSearcher'
import { degreesRules, degreesRules359, percloseRules, speedRules, timeRules } from '@/utils/formRules'
import StateSettings from './driverSettings/stateSettings'
import GeneralSettings from './driverSettings/generalSettings'
import snackbarMixin from '@/mixins/snackbarMixin'
import DriverSettingsSyncConfirmation from './driverSettingsSyncConfirmation'
import { DangerousState, SyncDialogStates } from '@/enum'
import { dangerousStateIcons } from '@/utils/icons'

const { mapFields: authFields } = createHelpers({
  getterType: `${AUTH_MODULE}/getField`
})

const defaultState = () => ({
  debug: false,
  requestLoading: false,
  isEdited: false,
  unsavedDialog: false,
  syncConfirmationDialog: false,
  originalSettings: new UserSettings() // we will store original settings from server here
})

const fineTuningDefault = () => {
  const settings = new UserSettings()
  const result = {}
  for (const field of Object.keys(settings.backendModel())) {
    result[field] = false
  }

  return result
}

const getNotificationSettings = (title, icon = accountIcon, switchField = null, numberField = null) => {
  const settings = {
    title,
    switchTitle: switchField ? 'notificationVocalize' : 'showServiceMessages',
    icon
  }

  if (switchField) {
    settings['switch'] = switchField
  }

  if (numberField) {
    settings['number'] = numberField
    settings['numberTitle'] = 'notificationInterval'
  }

  return settings
}

const accountIcon = mdiAccountCardDetails
const closeIcon = mdiClose
const notificationsIcon = mdiBellRing
const filtersIcon = mdiFilter
const settingsIcon = mdiCogs
const generalIcon = mdiFileSettings
const serviceMessageIcon = mdiBullhorn
const syncIcon = mdiSync

export default {
  name: 'driverSettingsDialog',
  components: { DriverSettingsSyncConfirmation, GeneralSettings, StateSettings, DriverSettingsSearcher },
  mixins: [snackbarMixin],
  props: {
    dialog: Boolean,
    loaded: Boolean,
    username: String,
    userId: Number,
    nanoUser: Boolean,
    isDefaultSettings: {
      type: Boolean,
      default: false
    },
    profile: UserProfile,
    settings: UserSettings
  },
  computed: {
    ...authFields(['isSpecial', 'isAdmin', 'isDebugView']),
    defaults () {
      return this.settings.defaults !== undefined ? this.settings.defaults : {}
    },
    buttonIcon () {
      return mdiContentSave
    },
    mobile () {
      return !(['xs', 'sm'].includes(this.$vuetify.breakpoint.name))
    },
    toolbarExtensionHeight () {
      return this.$vuetify.breakpoint.xsOnly ? 100 : undefined
    },
    isDebugAvailable () {
      return this.isSpecial || this.isDebugView
    },
    isDebug () {
      return this.isDebugAvailable && this.state.debug
    },

    // dynamic data fields
    statisticsList () {
      return [
        { field: 'driverStatisticsEnabled', type: 'switch' },
        { field: 'driverStatisticsSyncWifi', type: 'switch', show: !this.profile.nanoUser }
      ]
    },

    recommendationAndDangerousStateList () {
      return [
        { field: 'intervalBetweenRecommendationsSec', show: !this.profile.nanoUser },
        { field: 'travelTimeLastStopHours', step: 0.1, show: !this.profile.nanoUser },
        { field: 'mirrorImageDriver', type: 'switch' },
        { field: 'mirrorImageRoad', type: 'switch' },
        { field: 'isDetectCameraCalibrate', type: 'switch' },
        // { field: 'imageRotateAngle', type: 'slider', min: -359, max: 359 }
        { field: 'imageRotateAngle', rules: degreesRules359 }
      ]
    },

    detectorSettingsList () {
      return [
        { field: 'intervalBetweenDetectDangersBetween', step: 500 },
        { field: 'beltModelFramesPerSecond', rules: timeRules },
        { field: 'detectorLowLightThreshold', rules: timeRules, show: !this.profile.nanoUser }
      ]
    },

    detectorStopSettingsList () {
      return [
        { field: 'detectorStopRepeatCount', rules: timeRules },
        { field: 'detectorStopActivatePeriod', rules: timeRules },
        { field: 'detectorStopSuspendTime', rules: timeRules },
      ]
    },

    deviceList () {
      return [
        { field: 'audioLevelPref', type: 'slider', min: 0, max: 100 },
        { field: 'screenBrightness', type: 'slider', min: -1, max: 100, show: !this.profile.nanoUser },
        { field: 'trainModeEnabled', type: 'switch', show: !this.profile.nanoUser },
        { field: 'finishAppOnCloseNavig', type: 'switch', show: !this.profile.nanoUser },
        { field: 'isVibrate', type: 'switch', show: !this.profile.nanoUser },
        { field: 'showDebugInfoVideo', type: 'switch' }
      ]
    }
  },
  data: function () {
    return {
      dangerousStateIcons: dangerousStateIcons(),
      accountIcon: accountIcon,
      closeIcon: closeIcon,
      notificationsIcon: notificationsIcon,
      filtersIcon: filtersIcon,
      settingsIcon: settingsIcon,
      generalIcon: generalIcon,
      syncIcon: syncIcon,

      tab: null,
      state: defaultState(),
      fineTuningSettings: fineTuningDefault(),
      isFineTuning: false,

      // rules section
      timeRules: timeRules,
      degreesRules: degreesRules,
      degreesRules359: degreesRules359,
      percloseRules: percloseRules,

      drowsinessFilters: [
        { field: 'minSpeedDrowsiness', type: 'slider', min: 0, max: 100, css: 'mb-0' },
        { field: 'minFramesDetectDrowsiness' },
        { field: 'periodDetectDrowsiness' }
      ],

      drowsinessSettings: [
        { field: 'perclosPref', rules: percloseRules, step: 0.1 },
        { field: 'mouthWidthRatioSmile', rules: percloseRules, step: 0.1 },
        { field: 'headRotateAngleDrowsinessDetect', rules: degreesRules },
        { field: 'headTiltAngleDrowsinessDetect', rules: degreesRules },
        { field: 'leftEyeOpennessPercentagePref', type: 'slider', min: 0, max: 100 },
        { field: 'rightEyeOpennessPercentagePref', type: 'slider', min: 0, max: 100 },
        { field: 'confidenceDetectGazeRadio', rules: percloseRules, step: 0.1 },
        {},
        { label: '$vuetify.map.driverSettings.tabs.yawn' }, {},
        { field: 'thresholdMouthOpenDetect', rules: timeRules, step: 0.1 },
        { field: 'yawnCount', rules: timeRules },
        { field: 'yawnPeriodDetect', rules: timeRules },
        { field: 'timeDetectOneYawn', rules: timeRules },
        { field: 'yawnFramesRatioDetect', rules: percloseRules, step: 0.1 }
      ],

      distractionFilters: [
        // { field: 'periodDetectDistraction' },
        { field: 'minFramesDetectDistraction', mdCols: 6 },
        { field: 'distractionIntervalSpeedStart', mdCols: 6 },
        { field: 'distractionIntervalSpeedEnd', mdCols: 6 }
      ],
      distractionSettings: [
        { field: 'leftHeadAnglePref', rules: degreesRules },
        { field: 'rightHeadAnglePref', rules: degreesRules },
        { field: 'upHeadAnglePref', rules: degreesRules },
        { field: 'downHeadAnglePref', rules: degreesRules }
        // { field: 'autoCalibrateEnabled', type: 'switch' },
        // { field: 'calibratedHeadAngleYawPref', rules: degreesRules, canBeDisabled: true },
        // { field: 'calibratedHeadAnglePitchPref', rules: degreesRules, canBeDisabled: true }
      ],

      distractionNoFaceFilters: [
        { field: 'minSpeedDistractionNoFace', type: 'slider', mdCols: 6, min: 0, max: 100, css: 'mb-0' },
        { field: 'minFramesDetectDistraction', mdCols: 6 },
        { field: 'distractionNoFaceIntervalSpeedStart', label: 'distractionIntervalSpeedStart', mdCols: 6 },
        { field: 'distractionNoFaceIntervalTimeDetectStart', label: 'distractionIntervalTimeDetectStart', mdCols: 6 },
        { field: 'distractionNoFaceIntervalSpeedEnd', label: 'distractionIntervalSpeedEnd', mdCols: 6 },
        { field: 'distractionNoFaceIntervalTimeDetectEnd', label: 'distractionIntervalTimeDetectEnd', mdCols: 6 }
      ],

      distractionNoFaceSettings: [
        { field: 'ratioFramesDistractionNoFace', rules: percloseRules, mdCols: 6, step: 0.1 }
      ],

      distractionNoAttentionFilters: [
        // { field: 'maxFramesDetectDistraction', mdCols: 6 },
        { field: 'minSpeedDistractionNoAttention', type: 'slider', mdCols: 6, min: 0, max: 100, css: 'mb-0' },
        { field: 'minFramesDetectDistraction', mdCols: 6 },
        { field: 'distractionNoAttentionIntervalSpeedStart', label: 'distractionIntervalSpeedStart', mdCols: 6 },
        {
          field: 'distractionNoAttentionIntervalTimeDetectStart',
          label: 'distractionIntervalTimeDetectStart',
          mdCols: 6
        },
        { field: 'distractionNoAttentionIntervalSpeedEnd', label: 'distractionIntervalSpeedEnd', mdCols: 6 },
        { field: 'distractionNoAttentionIntervalTimeDetectEnd', label: 'distractionIntervalTimeDetectEnd', mdCols: 6 }
      ],

      distractionNoAttentionSettings: [
        { field: 'distractionNoAttentionLeftHeadAnglePref', label: 'leftHeadAnglePref', rules: degreesRules },
        { field: 'distractionNoAttentionRightHeadAnglePref', label: 'rightHeadAnglePref', rules: degreesRules },
        { field: 'distractionNoAttentionUpHeadAnglePref', label: 'upHeadAnglePref', rules: degreesRules },
        { field: 'distractionNoAttentionDownHeadAnglePref', label: 'downHeadAnglePref', rules: degreesRules },
        { field: 'ratioFramesDistractionNoAttention', rules: percloseRules, mdCols: 6, step: 0.1 }
      ],

      beltFilters: [
        { field: 'minSpeedBeltNotFasten', type: 'slider', min: 0, max: 100, css: 'mb-0' },
        { field: 'periodDetectBelt' }, { field: 'percentFramesDetectBelt' }
      ],

      beltSettings: [
        { field: 'confidenceDetectFrontBeltTop', rules: percloseRules, step: 0.1, mdCols: 4 },
        { field: 'confidenceDetectFrontBeltBottom', rules: percloseRules, step: 0.1, mdCols: 4 },
        {},
        { field: 'isDetectBackBelt', type: 'switch' },
        { mdCols: 6 },
        { field: 'confidenceDetectBackBeltTop', rules: percloseRules, step: 0.1, mdCols: 4 },
        { field: 'confidenceDetectBackBeltBottom', rules: percloseRules, step: 0.1, mdCols: 4 }

      ],

      sabotageFilters: [
        { field: 'minSpeedCameraSabotage', type: 'slider', min: 0, max: 100, css: 'mb-0' },
        { field: 'maxFramesDetectSabotage' }, { field: 'minFramesDetectSabotage' }, { field: 'periodDetectSabotage' }
      ],

      cellPhoneFilters: [
        { field: 'minSpeedCellPhone', type: 'slider', min: 0, max: 100, css: 'mb-0' },
        { field: 'periodDetectCellPhone' }, { field: 'percentFramesDetectPhone' }
      ],

      cellPhoneSettings: [
        { field: 'confidenceDetectCellPhone', rules: percloseRules, step: 0.1 }
      ],

      eatingFilters: [
        { field: 'minSpeedEating', type: 'slider', min: 0, max: 100, css: 'mb-0' },
        { field: 'periodDetectEating' }, { field: 'percentFramesDetectEating' }
      ],

      eatingSettings: [
        { field: 'confidenceDetectEating', rules: percloseRules, step: 0.1 }
      ],

      smokingFilters: [
        { field: 'minSpeedSmoking', type: 'slider', min: 0, max: 100, css: 'mb-0' },
        { field: 'periodDetectSmoking' }, { field: 'percentFramesDetectSmoking' }
      ],

      smokingSettings: [
        { field: 'confidenceDetectSmoking', rules: percloseRules, step: 0.1 }
      ],

      notificationsSettings: [
        getNotificationSettings('serviceMessages', serviceMessageIcon, 'showServiceMessages'),
        getNotificationSettings('drowsiness', dangerousStateIcons()[DangerousState.DROWSINESS], 'notificationDrowsinessTts', 'notificationDrowsinessInterval'),
        getNotificationSettings('distractionNoFace', dangerousStateIcons()[DangerousState.DISTRACTION_NO_FACE], 'notificationDistractionNoFaceTts', 'notificationDistractionNoFaceInterval'),
        getNotificationSettings('distractionNoAttention', dangerousStateIcons()[DangerousState.DISTRACTION_NO_ATTENTION], 'notificationDistractionNoAttentionTts', 'notificationDistractionNoAttentionInterval'),
        getNotificationSettings('belt', dangerousStateIcons()[DangerousState.BELT_NOT_FASTEN], 'notificationBeltTts', 'notificationBeltInterval'),
        getNotificationSettings('sabotage', dangerousStateIcons()[DangerousState.CAMERA_SABOTAGE], 'notificationSabotageTts', 'notificationSabotageInterval'),
        getNotificationSettings('cellphone', dangerousStateIcons()[DangerousState.CELLPHONE_USE], 'notificationCellPhoneTts', 'notificationCellPhoneInterval'),
        getNotificationSettings('eating', dangerousStateIcons()[DangerousState.EATING], 'notificationEatingTts', 'notificationEatingInterval'),
        getNotificationSettings('smoking', dangerousStateIcons()[DangerousState.SMOKING], 'notificationSmokingTts', 'notificationSmokingInterval'),
        getNotificationSettings('cameraOff', dangerousStateIcons()[DangerousState.CAMERA_OFF], 'notificationCameraOffTts', 'notificationCameraOffInterval')
      ],

      distraction: [
        'leftHeadAnglePref', 'rightHeadAnglePref', 'calibratedHeadAngleYawPref', 'calibratedHeadAnglePitchPref', 'upHeadAnglePref', 'downHeadAnglePref'
      ],
      drowsinessSliders: [
        'leftEyeOpennessPercentagePref', 'rightEyeOpennessPercentagePref'
      ],
      yawn: [
        'yawnCount', 'yawnPeriodDetect'
      ],
      generalSwitch: [
        'mirrorImage', 'driverStatisticsEnabled', 'driverStatisticsSyncWifi', 'trainModeEnabled', 'finishAppOnCloseNavig',
        'settingPlayBeepEvent', 'settingPlayBeepDangerEvent', 'isVibrate'
      ],

      generalText: [
        'travelTimeLastStopHours', 'intervalBetweenDetectDangersBetween', 'intervalBetweenRecommendationsSec'
      ],

      generalHeadCalibration: [
        { field: 'autoCalibrateEnabled', type: 'switch' },
        { field: 'calibratedHeadAngleYawPref', rules: degreesRules, canBeDisabled: true },
        { field: 'calibratedHeadAnglePitchPref', rules: degreesRules, canBeDisabled: true }
      ],

      soundList: [
        { field: 'settingPlayBeepEvent', type: 'switch' },
        { field: 'settingPlayBeepDangerEvent', type: 'switch' }
      ],

      autoCalibrateList: [
        {
          field: 'calibratedHeadAngleYawPref',
          rules: degreesRules,
          canBeDisabled: true,
          disableCondition: (settings) => settings['autoCalibrateEnabled'] === true
        },
        {
          field: 'calibratedHeadAnglePitchPref',
          rules: degreesRules,
          canBeDisabled: true,
          disableCondition: (settings) => settings['autoCalibrateEnabled'] === true
        },
        {
          field: 'mouthWidthCalibrated',
          rules: timeRules,
          canBeDisabled: true,
          step: 0.1,
          disableCondition: (settings) => settings['autoCalibrateEnabled'] === true
        },
        {
          field: 'autoCalibrateSpeed',
          rules: speedRules,
          canBeDisabled: true,
          disableCondition: (settings) => settings['autoCalibrateEnabled'] === false
        },
        {
          field: 'autoCalibrateFirstPeriod',
          rules: timeRules,
          canBeDisabled: true,
          disableCondition: (settings) => settings['autoCalibrateEnabled'] === false
        },
        {
          field: 'autoCalibrateRefineInterval',
          rules: timeRules,
          canBeDisabled: true,
          disableCondition: (settings) => settings['autoCalibrateEnabled'] === false
        }
      ]

    }
  },
  methods: {
    dialogCloseRequest () {
      this.state.unsavedDialog = false
      this.$emit('close-settings-dialog')
    },
    closeDialog () {
      if (!this.state.isEdited) {
        this.dialogCloseRequest()
      } else {
        // something is changed - ask again about
        this.state.unsavedDialog = true
      }
    },
    keyDown (data) {
      if (data.keyCode === 27) {
        this.closeDialog()
      }
    },
    getPlaceholder (field) {
      if (this.defaults !== undefined) {
        return field in this.defaults ? this.defaults[field].toString() : null
      } else {
        return ''
      }
    },

    async saveDriverSettings () {
      await backend.setDriverSettings({
        user_id: this.userId,
        profile_entry: {
          profile: this.profile.backendModel(),
          settings: this.settings.backendModel()
        }
      })
    },

    async saveDefaultSettings () {
      await backend.setDefaultSettings({
        profile_entry: {
          profile: this.profile.backendModel(),
          settings: this.settings.backendModel()
        }
      })
    },

    driverSyncSuccess () {
      this.openSuccessSnackbar('$vuetify.map.driverSettings.response.driverSyncOk')
    },

    driverSyncFailure () {
      this.openFailureSnackbar('$vuetify.map.driverSettings.response.driverSyncError')
    },

    async _sendProfile (successText = '$vuetify.map.driverSettings.response.ok', errorText = '$vuetify.map.driverSettings.response.error') {
      // try to save data
      try {
        this.state.requestLoading = true

        if (!this.isDefaultSettings) {
          await this.saveDriverSettings()
        } else {
          await this.saveDefaultSettings()
        }

        // now data is synced
        this.state.isEdited = false
        this.state.originalSettings = clone(this.settings)
        this.openSuccessSnackbar(successText)
      } catch (e) {
        console.error(e)
        this.openFailureSnackbar(errorText)
      } finally {
        this.state.requestLoading = false
      }
    },

    async processForm () {
      await this._sendProfile()
    },
    async syncProfile () {
      try {
        const result = await backend.fetchDefaultSettings()
        const defaultSettings = Object.keys(result.data).filter(item => !this.settings.excludedFields.includes(item)).reduce((obj, key) => ({
          ...obj,
          [key]: result.data[key]
        }), {})

        for (const field of Object.keys(defaultSettings)) {
          const checkedField = ['leftEyeOpennessPercentagePref', 'rightEyeOpennessPercentagePref'].includes(field) ? '_' + field : field
          if (this.settings.hasOwnProperty(checkedField) && this.settings[checkedField] !== defaultSettings[field]) {
            this.settings[checkedField] = defaultSettings[field]
          }
        }

        // send default settings to user
        await this._sendProfile('$vuetify.map.driverSettings.response.syncOk', '$vuetify.map.driverSettings.response.syncError')
      } catch (e) {
        console.error(e)
      }
    },
    fineTuningChanged (value) {
      if (!value) {
        this.fineTuningSettings = fineTuningDefault()
      }
      this.isFineTuning = value
    }
  },
  watch: {
    dialog (value) {
      if (value) {
        // if client reopen the dialog - just reinit it
        this.state = defaultState()
        this.isFineTuning = false
        this.fineTuningSettings = fineTuningDefault()
      }
    },
    settings: {
      deep: true,
      handler (value) {
        if (this.dialog && this.loaded) {
          if (this.state.originalSettings.idSettings === -1) {
            // original settings are not saved yet
            this.state.originalSettings = clone(value)
          } else {
            // can check changes
            this.state.isEdited = !isEqual(value.backendModel(), this.state.originalSettings.backendModel())
          }
        }
      }
    }
  },
  created () {
    this['SyncDialogStates'] = SyncDialogStates
    this['DangerousState'] = DangerousState
  }
}
</script>

<style scoped>
>>> .v-dialog {
  box-shadow: none;
}

>>> .v-input__slider .v-input__slot .v-label {
  max-width: 250px;
}
</style>
